import { Ruler, ShieldCheck, Clock, Handshake } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, MaskHeading } from "@/components/site/motion";
import { Marquee } from "@/components/site/Marquee";
import { QuoteCTA } from "@/components/site/QuoteCTA";
import { useSeo } from "@/components/site/Seo";
import { BUSINESS } from "@/config/businessConfig";

const VALUES = [
  { icon: Ruler, t: "Square & level", d: "We take the time to build it properly — tight joints, level mounts, every part where it belongs." },
  { icon: ShieldCheck, t: "Respect for your home", d: "We treat your space like our own and leave it tidy when the job is done." },
  { icon: Clock, t: "On your schedule", d: "We work with your timing and keep you posted on when we'll arrive." },
  { icon: Handshake, t: "Straight answers", d: "A clear quote up front, and honest advice if something isn't a good fit." },
];

export default function About() {
  useSeo({
    title: "About Assemble Pro | Local Assembly & Installation in Woodstock, Ontario",
    description: "Assemble Pro is a Woodstock, Ontario assembly and installation business serving Ingersoll and most of Oxford County. Learn who we are and how we work.",
    path: "/about",
  });
  return (
    <div data-testid="about-page">
      <PageHero testId="about-hero" eyebrow="About · Woodstock, Ontario"
        lines={[{ text: "Local hands." }, { text: "Serious tools.", className: "text-[var(--orange)]" }]}
        intro="Assemble Pro was built on a simple idea: you shouldn't have to spend your weekend fighting an instruction manual. You buy it. We assemble it. We install it."
        img="/img/assembly.jpg" imgAlt="Assembler attaching a bracket with a screwdriver" />

      <section className="bg-[var(--ink)] py-20 lg:py-32" data-testid="about-story">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-5">Our story</p>
            <MaskHeading lines={[{ text: "Rooted in" }, { text: "Oxford County." }]} className="font-display font-black uppercase text-5xl sm:text-6xl leading-[0.9]" />
          </div>
          <Reveal className="lg:col-span-7 space-y-6 text-zinc-300 text-base lg:text-lg leading-relaxed">
            <p>
              Based in Woodstock and serving Ingersoll and most of Oxford County, Assemble Pro helps homeowners, renters and businesses get the things they buy built and installed — properly, and without the hassle.
            </p>
            <p>
              From a single desk to a backyard full of projects — a gazebo, a shed, a new BBQ and a gate that finally latches — we handle practically anything that comes in a box or needs to go on a wall.
            </p>
            <p className="border-l-2 border-[var(--orange)] pl-5 text-zinc-200" data-testid="about-owner-placeholder">
              [Add your story here: who you are, your background and experience, and why you started Assemble Pro. — {BUSINESS.ownerName}]
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="bg-[var(--char)] py-20 lg:py-32" data-testid="about-values">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
          <p className="eyebrow mb-5">How we work</p>
          <MaskHeading lines={[{ text: "The Assemble Pro" }, { text: "standard.", className: "text-[var(--orange)]" }]} className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] mb-14" />
          <div className="grid gap-px bg-[var(--line)] border border-[var(--line)] sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.08} className="bg-[var(--char)] p-8 lg:p-10 min-h-[260px] hover:bg-[var(--ink)] transition-colors duration-500">
                <v.icon className="w-9 h-9 text-[var(--orange)]" strokeWidth={1.75} />
                <h3 className="mt-10 font-display font-black uppercase text-3xl leading-none">{v.t}</h3>
                <p className="mt-3 text-zinc-300 text-sm leading-relaxed">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <QuoteCTA title={["Let's build", "something."]} />
    </div>
  );
}
